import { useState, useRef, useCallback } from 'react'
import type { FoundryAsset, AssetType } from '../types'
import { AssetCard } from './AssetCard'
import { AssetDetail } from './AssetDetail'
import { TagFilter } from './TagFilter'

interface Props {
  assets: FoundryAsset[]
  loading: boolean
  filterType: AssetType | null
  setFilterType: (t: AssetType | null) => void
  filterTags: string[]
  setFilterTags: (tags: string[]) => void
  searchQuery: string
  setSearchQuery: (q: string) => void
  onUpdate: (id: string, updates: { name?: string; tags?: string[] }) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

export function AssetBrowser({
  assets, loading, filterType, setFilterType, filterTags, setFilterTags,
  searchQuery, setSearchQuery, onUpdate, onDelete,
}: Props) {
  const [detailAsset, setDetailAsset] = useState<FoundryAsset | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setSearchQuery(val), 300)
  }, [setSearchQuery])

  const toggleTag = (tag: string) => {
    setFilterTags(
      filterTags.includes(tag)
        ? filterTags.filter((t) => t !== tag)
        : [...filterTags, tag],
    )
  }

  const characters = assets.filter((a) => a.type === 'character')
  const scenes = assets.filter((a) => a.type === 'scene')

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-forge-slate/50">
        {/* Type tabs */}
        <div className="flex rounded-md border border-forge-slate overflow-hidden">
          {(['all', 'character', 'scene'] as const).map((t) => {
            const active = t === 'all' ? !filterType : filterType === t
            return (
              <button
                key={t}
                onClick={() => setFilterType(t === 'all' ? null : t)}
                className={`text-[11px] font-semibold px-3 py-1.5 transition-colors ${
                  active
                    ? 'bg-forge-ember text-white'
                    : 'text-forge-ash hover:text-forge-primary hover:bg-forge-basalt'
                }`}
              >
                {t === 'all' ? 'All' : t === 'character' ? 'Characters' : 'Scenes'}
              </button>
            )
          })}
        </div>

        {/* Search */}
        <div className="flex-1 max-w-xs">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search assets..."
            defaultValue={searchQuery}
            onChange={handleSearchChange}
            className="w-full bg-forge-obsidian border border-forge-slate rounded-md px-3 py-1.5 text-[12px] text-forge-primary placeholder:text-forge-cool-smoke focus:border-forge-ember focus:outline-none"
          />
        </div>

        {/* Filter toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`text-[11px] font-semibold rounded-md px-3 py-1.5 border transition-colors ${
            showFilters || filterTags.length > 0
              ? 'border-forge-ember text-forge-ember'
              : 'border-forge-slate text-forge-ash hover:text-forge-primary'
          }`}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline mr-1">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          Filters {filterTags.length > 0 && `(${filterTags.length})`}
        </button>

        {filterTags.length > 0 && (
          <button
            onClick={() => setFilterTags([])}
            className="text-[10px] text-forge-heat-critical hover:text-forge-heat-critical/80 font-semibold"
          >
            Clear
          </button>
        )}

        <span className="text-[10px] text-forge-cool-smoke ml-auto">{assets.length} asset{assets.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="px-5 py-3 border-b border-forge-slate/50 bg-forge-obsidian/30">
          <TagFilter type={filterType} selectedTags={filterTags} onToggleTag={toggleTag} compact />
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 overflow-auto p-5">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-6 h-6 border-2 border-forge-ember border-t-transparent rounded-full animate-spin" />
          </div>
        ) : assets.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-center">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-forge-cool-smoke mb-3">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            <p className="text-[12px] text-forge-ash">No assets yet</p>
            <p className="text-[10px] text-forge-cool-smoke mt-1">Switch to the Create tab to generate your first character or scene</p>
          </div>
        ) : filterType ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {assets.map((a) => (
              <AssetCard key={a.id} asset={a} onView={() => setDetailAsset(a)} />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {characters.length > 0 && (
              <section>
                <h3 className="text-[11px] font-bold text-forge-ember uppercase tracking-wider mb-3">
                  Characters ({characters.length})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {characters.map((a) => (
                    <AssetCard key={a.id} asset={a} onView={() => setDetailAsset(a)} />
                  ))}
                </div>
              </section>
            )}
            {scenes.length > 0 && (
              <section>
                <h3 className="text-[11px] font-bold text-forge-gold uppercase tracking-wider mb-3">
                  Scenes ({scenes.length})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {scenes.map((a) => (
                    <AssetCard key={a.id} asset={a} onView={() => setDetailAsset(a)} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detailAsset && (
        <AssetDetail
          asset={detailAsset}
          onClose={() => setDetailAsset(null)}
          onUpdate={onUpdate}
          onDelete={onDelete}
        />
      )}
    </div>
  )
}
